import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { AdminService } from '@/types';

export interface ServiceFormData {
  name: string;
  price: string;
  duration_minutes: string;
  short_description: string;
  landing_description: string;
  booking_fee: string;
  active: boolean;
}

const emptyForm: ServiceFormData = {
  name: '',
  price: '',
  duration_minutes: '',
  short_description: '',
  landing_description: '',
  booking_fee: '0',
  active: true,
};

export function useServices() {
  const [services, setServices] = useState<AdminService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('services')
      .select('id, name, price, duration_minutes, description, short_description, landing_description, booking_fee, active, created_at')
      .order('created_at', { ascending: true });

    if (err) {
      setError(err.message);
    } else {
      setServices((data ?? []) as AdminService[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const createService = useCallback(async (form: ServiceFormData): Promise<string | null> => {
    setSaving(true);
    const { error: err } = await supabase.from('services').insert({
      name: form.name.trim(),
      price: parseFloat(form.price),
      duration_minutes: parseInt(form.duration_minutes, 10),
      short_description: form.short_description.trim() || null,
      landing_description: form.landing_description.trim() || null,
      booking_fee: parseFloat(form.booking_fee) || 0,
      active: form.active,
    });
    setSaving(false);
    if (err) return err.message;
    await fetchServices();
    return null;
  }, [fetchServices]);

  const updateService = useCallback(async (id: string, form: ServiceFormData): Promise<string | null> => {
    setSaving(true);
    const { error: err } = await supabase
      .from('services')
      .update({
        name: form.name.trim(),
        price: parseFloat(form.price),
        duration_minutes: parseInt(form.duration_minutes, 10),
        short_description: form.short_description.trim() || null,
        landing_description: form.landing_description.trim() || null,
        booking_fee: parseFloat(form.booking_fee) || 0,
        active: form.active,
      })
      .eq('id', id);
    setSaving(false);
    if (err) return err.message;
    await fetchServices();
    return null;
  }, [fetchServices]);

  const updateBookingFee = useCallback(async (id: string, fee: number): Promise<string | null> => {
    const { error: err } = await supabase
      .from('services')
      .update({ booking_fee: fee })
      .eq('id', id);
    if (err) return err.message;
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, booking_fee: fee } : s)));
    return null;
  }, []);

  const toggleActive = useCallback(async (id: string, active: boolean) => {
    setServices((prev) =>
      prev.map((s) => (s.id === id ? { ...s, active } : s))
    );
    await supabase.from('services').update({ active }).eq('id', id);
  }, []);

  return {
    services,
    loading,
    error,
    saving,
    refetch: fetchServices,
    createService,
    updateService,
    updateBookingFee,
    toggleActive,
    emptyForm,
  };
}
